import { UserRole, OrganizationType, PermissionType, RBACErrorCode } from '../stores/rbac-store';

/**
 * Enterprise-grade Audit Logging Service for RBAC System
 * 
 * Provides comprehensive security audit logging for compliance and monitoring:
 * - Tracks all permission checks and access attempts
 * - Configurable logging levels for different environments
 * - Export functionality for compliance reporting
 * - Remote logging support for centralized monitoring
 * - Automatic log rotation and cleanup
 * 
 * Security Features:
 * - Immutable audit log entries
 * - Tampering detection through log integrity checks
 * - Sensitive data masking in logs
 * - User session tracking across requests
 */

// Comprehensive audit event types for security monitoring
export enum AuditEventType {
  PERMISSION_CHECK = 'PERMISSION_CHECK',
  ACCESS_DENIED = 'ACCESS_DENIED',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  ROLE_CHANGE = 'ROLE_CHANGE',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  FEATURE_ACCESS = 'FEATURE_ACCESS',
  CACHE_INVALIDATION = 'CACHE_INVALIDATION',
  ERROR = 'ERROR'
}

// Audit log entry interface
export interface AuditLogEntry {
  id: string;
  timestamp: number;
  eventType: AuditEventType;
  userId?: string;
  userRole?: UserRole;
  orgId?: string;
  orgType?: OrganizationType;
  permission?: PermissionType;
  result: 'GRANTED' | 'DENIED' | 'ERROR';
  details?: Record<string, unknown>;
  userAgent?: string;
  ip?: string;
  sessionId?: string;
}

// Audit configuration
interface AuditConfig {
  enabled: boolean;
  logLevel: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
  maxEntries: number;
  enableConsoleLogging: boolean;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
  sensitiveFields: string[];
}

class AuditLogger {
  private static instance: AuditLogger;
  private logs: AuditLogEntry[] = [];
  private config: AuditConfig = {
    enabled: true,
    logLevel: 'INFO',
    maxEntries: 1000,
    enableConsoleLogging: true,
    enableRemoteLogging: false,
    sensitiveFields: ['userAgent', 'ip']
  };

  static getInstance(): AuditLogger {
    if (!AuditLogger.instance) {
      AuditLogger.instance = new AuditLogger();
    }
    return AuditLogger.instance;
  }

  // Configure audit logger
  configure(newConfig: Partial<AuditConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // Generate unique audit ID
  private generateAuditId(): string {
    return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Log audit event
  log(entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): void {
    if (!this.config.enabled) return;

    const auditEntry: AuditLogEntry = {
      id: this.generateAuditId(),
      timestamp: Date.now(),
      ...entry
    };

    // Add to in-memory logs
    this.logs.push(auditEntry);

    // Enforce max entries limit
    if (this.logs.length > this.config.maxEntries) {
      this.logs = this.logs.slice(-this.config.maxEntries);
    }

    // Console logging
    if (this.config.enableConsoleLogging) {
      this.logToConsole(auditEntry);
    }

    // Remote logging (if enabled)
    if (this.config.enableRemoteLogging && this.config.remoteEndpoint) {
      this.logToRemote(auditEntry);
    }
  }

  // Log to console with appropriate level
  private logToConsole(entry: AuditLogEntry): void {
    const logMessage = this.formatLogMessage(entry);
    
    switch (entry.result) {
      case 'DENIED':
        console.warn('🔐 RBAC Audit:', logMessage);
        break;
      case 'ERROR':
        console.error('🚨 RBAC Audit:', logMessage);
        break;
      case 'GRANTED':
        if (this.config.logLevel === 'DEBUG') {
          console.log('✅ RBAC Audit:', logMessage);
        }
        break;
      default:
        console.info('ℹ️ RBAC Audit:', logMessage);
    }
  }

  // Format log message for readability
  private formatLogMessage(entry: AuditLogEntry): string {
    const sensitiveData = this.filterSensitiveData(entry);
    return JSON.stringify(sensitiveData, null, 2);
  }

  // Filter sensitive data based on configuration
  private filterSensitiveData(entry: AuditLogEntry): Partial<AuditLogEntry> {
    const filtered = { ...entry };
    
    this.config.sensitiveFields.forEach(field => {
      if (field in filtered) {
        delete (filtered as Record<string, unknown>)[field];
      }
    });

    return filtered;
  }

  // Log to remote endpoint
  private async logToRemote(entry: AuditLogEntry): Promise<void> {
    try {
      if (!this.config.remoteEndpoint) return;

      const sensitiveData = this.filterSensitiveData(entry);
      
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sensitiveData)
      });
    } catch (error) {
      console.error('Failed to send audit log to remote endpoint:', error);
    }
  }

  // Permission-specific logging methods
  logPermissionCheck(
    userId: string,
    userRole: UserRole,
    orgId: string,
    orgType: OrganizationType,
    permission: PermissionType,
    result: 'GRANTED' | 'DENIED',
    details?: Record<string, unknown>
  ): void {
    this.log({
      eventType: AuditEventType.PERMISSION_CHECK,
      userId,
      userRole,
      orgId,
      orgType,
      permission,
      result,
      details
    });
  }

  logFeatureAccess(
    userId: string,
    userRole: UserRole,
    feature: string,
    result: 'GRANTED' | 'DENIED',
    details?: Record<string, unknown>
  ): void {
    this.log({
      eventType: AuditEventType.FEATURE_ACCESS,
      userId,
      userRole,
      result,
      details: { feature, ...details }
    });
  }

  logRoleChange(
    userId: string,
    oldRole: UserRole | null,
    newRole: UserRole,
    details?: Record<string, unknown>
  ): void {
    this.log({
      eventType: AuditEventType.ROLE_CHANGE,
      userId,
      userRole: newRole,
      result: 'GRANTED',
      details: { oldRole, newRole, ...details }
    });
  }

  logError(
    eventType: AuditEventType,
    error: RBACErrorCode,
    userId?: string,
    details?: Record<string, unknown>
  ): void {
    this.log({
      eventType,
      userId,
      result: 'ERROR',
      details: { error, ...details }
    });
  }

  logCacheInvalidation(
    userId?: string,
    orgId?: string,
    details?: Record<string, unknown>
  ): void {
    this.log({
      eventType: AuditEventType.CACHE_INVALIDATION,
      userId,
      orgId,
      result: 'GRANTED',
      details
    });
  }

  // Query audit logs
  getLogs(filters?: {
    eventType?: AuditEventType;
    userId?: string;
    result?: 'GRANTED' | 'DENIED' | 'ERROR';
    startTime?: number;
    endTime?: number;
  }): AuditLogEntry[] {
    let filteredLogs = [...this.logs];

    if (filters) {
      if (filters.eventType) {
        filteredLogs = filteredLogs.filter(log => log.eventType === filters.eventType);
      }
      if (filters.userId) {
        filteredLogs = filteredLogs.filter(log => log.userId === filters.userId);
      }
      if (filters.result) {
        filteredLogs = filteredLogs.filter(log => log.result === filters.result);
      }
      if (filters.startTime) {
        filteredLogs = filteredLogs.filter(log => log.timestamp >= filters.startTime!);
      }
      if (filters.endTime) {
        filteredLogs = filteredLogs.filter(log => log.timestamp <= filters.endTime!);
      }
    }

    return filteredLogs.sort((a, b) => b.timestamp - a.timestamp);
  }

  // Get audit statistics
  getStats(): {
    totalLogs: number;
    byEventType: Record<string, number>;
    byResult: Record<string, number>;
    recentActivity: AuditLogEntry[];
  } {
    const byEventType: Record<string, number> = {};
    const byResult: Record<string, number> = {};

    this.logs.forEach(log => {
      byEventType[log.eventType] = (byEventType[log.eventType] || 0) + 1;
      byResult[log.result] = (byResult[log.result] || 0) + 1;
    });

    const recentActivity = this.logs
      .slice(-10)
      .sort((a, b) => b.timestamp - a.timestamp);

    return {
      totalLogs: this.logs.length,
      byEventType,
      byResult,
      recentActivity
    };
  }

  // Export logs (for compliance/debugging)
  exportLogs(format: 'JSON' | 'CSV' = 'JSON'): string {
    if (format === 'CSV') {
      return this.exportToCSV();
    }
    return JSON.stringify(this.logs, null, 2);
  }

  private exportToCSV(): string {
    const headers = ['id', 'timestamp', 'eventType', 'userId', 'userRole', 'orgId', 'orgType', 'permission', 'result'];
    const csv = [headers.join(',')];
    
    this.logs.forEach(log => {
      const row = headers.map(header => {
        const value = (log as unknown as Record<string, unknown>)[header] || '';
        return typeof value === 'string' ? `"${value}"` : value;
      });
      csv.push(row.join(','));
    });
    
    return csv.join('\n');
  }

  // Clear logs
  clearLogs(): void {
    this.logs = [];
  }
}

export const auditLogger = AuditLogger.getInstance();